// « Générer le rapport de diagnostic » (offre Diagnostic) : l'API produit le PDF à partir de la carte telle qu'elle est
// (chiffres du moteur, citations revérifiées, règle éditoriale) et le garde ; l'application ne rédige rien, elle
// déclenche la génération et télécharge le PDF tel que l'API l'a produit. Présent sur le tableau de bord et l'écran
// « Documents ».
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { expliquerErreur } from '../api/client';
import { useEkera } from '../contexte';
import { dateFr, nomDePersonne } from '../domaine/format';
import { telecharger } from '../domaine/telechargement';
import { typo } from '../domaine/typo';
import { lienVers } from '../routeur';

export function RapportDiagnostic({ compact = false }: { compact?: boolean }) {
  const { client, cid, ecritureAutorisee } = useEkera();
  const qc = useQueryClient();
  const liste = useQuery({ queryKey: ['carte', cid, 'rapports'], queryFn: () => client.rapports(cid) });
  const generer = useMutation({
    mutationFn: () => client.genererRapport(cid),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['carte', cid, 'rapports'] }),
  });
  const charger = useMutation({
    mutationFn: async (rid: string) => telecharger(await client.telechargerRapport(cid, rid), `rapport-diagnostic-${cid}-${rid}.pdf`),
  });
  const rapports = liste.data ?? [];
  const dernier = rapports[0];
  return (
    <section className="panneau" aria-labelledby="t-rapport" data-testid="rapport-diagnostic">
      <div className="panneau-tete">
        <h2 id="t-rapport">Rapport de diagnostic</h2>
        {ecritureAutorisee && (
          <button type="button" className="btn principal" disabled={generer.isPending} onClick={() => generer.mutate()}>
            {generer.isPending ? 'Génération…' : 'Générer le rapport de diagnostic'}
          </button>
        )}
      </div>
      <p className="note">
        Synthèse, carte, points uniques, scénarios, pertes potentielles estimées (conditionnelles aux hypothèses retenues),
        indicateur de progression NIS2, scénarios d’investissement proposés à valider par l’entreprise, sensibilité et points à
        confirmer, d’où viennent les chiffres, et l’annexe des citations revérifiées. Chiffres du moteur seulement.
      </p>
      {generer.error && (
        <p role="alert" className="encadre attention">
          {expliquerErreur(generer.error)}
        </p>
      )}
      {charger.error && (
        <p role="alert" className="encadre attention">
          {expliquerErreur(charger.error)}
        </p>
      )}
      {rapports.length === 0 ? (
        <>
          <p>Aucun rapport n’a encore été généré pour cette carte.</p>
          <p className="encadre info" data-testid="rapport-vide">
            {typo('Le rapport reprend la carte telle qu’elle est au moment où vous le générez : il est le plus utile une fois les pièces lues et l’entretien fait. Vous pourrez en générer un nouveau quand la carte changera ; les précédents restent téléchargeables.')}{' '}
            <a href={lienVers('depot')}>Déposer des pièces</a> · <a href={lienVers('entretien')}>Répondre à l’entretien</a>
          </p>
        </>
      ) : (
        <ul className="liste-points" aria-label="Rapports générés">
          {(compact ? [dernier] : rapports).map(
            (r) =>
              r && (
                <li key={r.id}>
                  Rapport du {dateFr(r.genere_le)} (carte à la révision {r.revision_carte})
                  {nomDePersonne(r.genere_par) ? `, généré par ${nomDePersonne(r.genere_par)}` : ''} : {r.verification.verifiees} citations revérifiées
                  {r.verification.non_verifiees ? `, ${r.verification.non_verifiees} non retrouvées` : ''}
                  {r.verification.non_verifiables ? `, ${r.verification.non_verifiables} sans pièce disponible` : ''} ;{' '}
                  {r.points_a_confirmer} point{r.points_a_confirmer > 1 ? 's' : ''} à confirmer.{' '}
                  <button type="button" className="btn petit" disabled={charger.isPending} onClick={() => charger.mutate(r.id)}>
                    Télécharger le PDF
                  </button>
                </li>
              ),
          )}
        </ul>
      )}
    </section>
  );
}
